import { isoWeekOf, mondayOf } from './iso-week.js';
import type { CalendarDate } from './iso-week.js';

/**
 * Kalendertage (AC-103): Tagesarithmetik und das Plausibilitaetsfenster.
 *
 * Kalenderwochen, Wochenstarts und Wochentage kommen aus `iso-week.ts`
 * (AC-109, Kernregel 9) — hier steht KEINE eigene Wochenrechnung. `weekStartOf`
 * und `isoWeekday` sind aus `isoWeekOf` und `mondayOf` zusammengesetzt, damit
 * die Kapazitaet je Tag (AC-103) dieselbe Donnerstagsregel benutzt wie jede
 * andere Kalenderwoche im System.
 *
 * Gerechnet wird ausschliesslich auf Kalendertagen `YYYY-MM-DD`, nie auf
 * Zeitpunkten; intern ueber UTC-Mitternacht. „Heute" bestimmt allein
 * `calendarDateIn` aus `iso-week.ts`, in der fachlichen Zeitzone.
 */

/** Ein Kalendertag `YYYY-MM-DD` — derselbe Typ wie `CalendarDate` aus `iso-week.ts`. */
export type IsoDate = CalendarDate;

/**
 * Das Plausibilitaetsfenster fuer fachliche Daten (Eintritt, Austritt,
 * Gueltig-ab) — die Schemata dazu sind `planningDateSchema` und
 * `planningWeekSchema` in `common.ts`.
 *
 * Keine Geschaeftsregel, sondern Schutz vor Tippfehlern wie `0202-10-01` oder
 * `9999-12-31`: niemand ist vor 1900 eingetreten, und eine Planung ueber das
 * Jahr 2999 hinaus gibt es nicht. Nebenbei haelt das Fenster jede Rechnung im
 * vierstelligen Jahresbereich, in dem `toISOString` die Form `YYYY-MM-DD`
 * liefert.
 */
export const PLANNING_DATE_MIN = '1900-01-01';
export const PLANNING_DATE_MAX = '2999-12-31';
export const PLANNING_YEAR_MIN = 1900;
export const PLANNING_YEAR_MAX = 2999;

const MS_PER_DAY = 86_400_000;

/**
 * UTC-Mitternacht eines Kalendertags.
 *
 * `setUTCFullYear` und nicht `Date.UTC(y, …)`: `Date.UTC` legt die Jahre 0–99
 * auf 1900–1999 — dieselbe Falle, die `isValidIsoWeek` umgeht.
 */
function toUtc(date: IsoDate): Date {
  const [year, month, day] = date.split('-').map(Number);
  const value = new Date(0);
  value.setUTCFullYear(year ?? Number.NaN, (month ?? Number.NaN) - 1, day ?? Number.NaN);
  return value;
}

function fromUtc(value: Date): IsoDate {
  return value.toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const value = toUtc(date);
  value.setUTCDate(value.getUTCDate() + days);
  return fromUtc(value);
}

/** Tage von `from` bis `to`; negativ, wenn `to` davor liegt. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / MS_PER_DAY);
}

/** Der Montag der Kalenderwoche, in der `date` liegt (`iso-week.ts`). */
export function weekStartOf(date: IsoDate): IsoDate {
  return mondayOf(isoWeekOf(date));
}

/** Der Wochentag nach ISO 8601: 1 = Montag … 7 = Sonntag — gezaehlt ab dem Montag der Woche. */
export function isoWeekday(date: IsoDate): number {
  return 1 + daysBetween(weekStartOf(date), date);
}
